"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.getPopoverPosition = exports.getPaddingStyles = exports.getCordinates = void 0;
var _defineProperty2 = _interopRequireDefault(require("@babel/runtime/helpers/defineProperty"));
var _constants = require("./constants");
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { (0, _defineProperty2.default)(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
// Provides anchor point coordinates of given rect
const CORDINATES_BY_ANCHOR = {
  [_constants.ANCHOR.left_top]: ({
    x,
    y
  }) => ({
    x,
    y
  }),
  [_constants.ANCHOR.center_top]: ({
    x,
    y,
    width
  }) => ({
    x: x + width / 2,
    y
  }),
  [_constants.ANCHOR.right_top]: ({
    x,
    y,
    width
  }) => ({
    x: x + width,
    y
  }),
  [_constants.ANCHOR.left_center]: ({
    x,
    y,
    height
  }) => ({
    x,
    y: y + height / 2
  }),
  [_constants.ANCHOR.right_center]: ({
    x,
    y,
    width,
    height
  }) => ({
    x: x + width,
    y: y + height / 2
  }),
  [_constants.ANCHOR.left_bottom]: ({
    x,
    y,
    height
  }) => ({
    x,
    y: y + height
  }),
  [_constants.ANCHOR.center_bottom]: ({
    x,
    y,
    width,
    height
  }) => ({
    x: x + width / 2,
    y: y + height
  }),
  [_constants.ANCHOR.right_bottom]: ({
    x,
    y,
    width,
    height
  }) => ({
    x: x + width,
    y: y + height
  })
};

// Provides relative shift coordinates by width and height of given rect
const SHIFT_CORDINATES_BY_ANCHOR = {
  [_constants.ANCHOR.left_top]: () => ({
    x: 0,
    y: 0
  }),
  [_constants.ANCHOR.center_top]: ({
    width
  }) => ({
    x: -width / 2,
    y: 0
  }),
  [_constants.ANCHOR.right_top]: ({
    width
  }) => ({
    x: -width,
    y: 0
  }),
  [_constants.ANCHOR.left_center]: ({
    height
  }) => ({
    x: 0,
    y: -height / 2
  }),
  [_constants.ANCHOR.right_center]: ({
    width,
    height
  }) => ({
    x: -width,
    y: -height / 2
  }),
  [_constants.ANCHOR.left_bottom]: ({
    height
  }) => ({
    x: 0,
    y: -height
  }),
  [_constants.ANCHOR.center_bottom]: ({
    width,
    height
  }) => ({
    x: -width / 2,
    y: -height
  }),
  [_constants.ANCHOR.right_bottom]: ({
    width,
    height
  }) => ({
    x: -width,
    y: -height
  })
};
function findByClosestCoordinates(items, {
  x: desiredX,
  y: desiredY
}) {
  let closestItem = null;
  let closestDistance = Infinity;

  // Don't know why, but eslint thinks that item isn't used
  // eslint-disable-next-line no-unused-vars
  for (const item of items) {
    const distance = Math.sqrt((item.cordinates.x - desiredX) ** 2 + (item.cordinates.y - desiredY) ** 2);
    if (distance < closestDistance) {
      closestItem = item;
      closestDistance = distance;
    }
  }
  return closestItem;
}
const isRectWithinBounds = (rect, bounds) => rect.x >= bounds.x && rect.y >= bounds.y && rect.x + rect.width <= bounds.x + bounds.width && rect.y + rect.height <= bounds.y + bounds.height;
const isRectOutsideBounds = (rect, bounds) => rect.x + rect.width <= bounds.x || rect.y + rect.height <= bounds.y || rect.x >= bounds.x + bounds.width || rect.y >= bounds.y + bounds.height;
const getCordinates = ({
  anchors,
  rects
}) => {
  const bindCordinates = CORDINATES_BY_ANCHOR[anchors.binding](rects.binding);
  const coordinatesShift = SHIFT_CORDINATES_BY_ANCHOR[anchors.content](rects.content);
  return {
    x: bindCordinates.x + coordinatesShift.x,
    y: bindCordinates.y + coordinatesShift.y
  };
};

/**
 * Calculates the ideal position of a popover element relative to its binding and content elements,
 * within the boundaries of a specified element. Various anchor positions and their corresponding
 * coordinates are tested to find a valid position that does not overlap the binding element and is
 * within the boundaries of the specified element. If no valid position is found, the last valid
 * position is returned.
 *
 * @param {Object} options - An object containing the following properties:
 *   @param {Element} options.bindingElement - The element that the popover is anchored to.
 *   @param {Element} options.boundaryElement - The element that the popover cannot overlap.
 *   @param {Element} options.contentElement - The element that contains the popover content.
 *   @param {Object} options.desiredAnchors - An object containing the desired binding and content
 *     anchor positions.
 *   @param {Object} options.lastAnchors - An object containing the last valid binding and content
 *     anchor positions.
 *   @param {boolean} [options.allowOverlap=false] - Whether to allow the popover to overlap the
 *     binding element.
 * @return {Object} An object containing the following properties:
 *   @param {Object} cordinates - An object containing the x and y coordinates of the popover.
 *   @param {Object} anchors - An object containing the binding and content anchor positions.
 */
exports.getCordinates = getCordinates;
const getPopoverPosition = ({
  bindingElement,
  boundaryElement,
  contentElement,
  desiredAnchors,
  lastAnchors,
  allowOverlap = false,
  verticalPlacingOnly = false,
  coords = null,
  fitToBindingElementWidth
}) => {
  const bindingRect = coords ? {
    y: coords.y,
    x: coords.x,
    width: 0,
    height: 0
  } : bindingElement.getBoundingClientRect();
  const contentRect = contentElement.getBoundingClientRect();
  const rects = {
    binding: bindingRect,
    content: {
      x: contentRect.x,
      y: contentRect.y,
      height: contentRect.height,
      width: fitToBindingElementWidth ? bindingRect.width : contentRect.width
    }
  };
  let boundingRect = boundaryElement.getBoundingClientRect();

  // Limits bounds to disable placing to the left or right sides relatively to the binding element
  if (verticalPlacingOnly) {
    boundingRect = {
      y: boundingRect.y,
      x: bindingRect.x,
      height: boundingRect.height,
      width: bindingRect.width
    };
  }

  // Possible anchor variants within bounding element
  const availablePositions = [];
  let desiredResult;
  Object.keys(CORDINATES_BY_ANCHOR).forEach(binding => {
    Object.keys(SHIFT_CORDINATES_BY_ANCHOR).forEach(content => {
      const anchors = {
        binding,
        content
      };
      const cordinates = getCordinates({
        anchors,
        rects
      });
      const positionItem = {
        anchors,
        cordinates
      };
      if (anchors.binding === desiredAnchors.binding && anchors.content === desiredAnchors.content) {
        desiredResult = positionItem;
      }

      // Our positioned rect content candidate
      const rect = _objectSpread(_objectSpread({}, cordinates), {}, {
        width: rects.content.width,
        height: rects.content.height
      });

      // If our content rect overlaps binding rect then skipp this variant
      if (!allowOverlap && !isRectOutsideBounds(rect, rects.binding)) return;
      if (!isRectWithinBounds(rect, boundingRect)) return;
      availablePositions.push(positionItem);
    });
  });
  const result = isRectWithinBounds(desiredResult.cordinates, boundingRect) ? desiredResult : findByClosestCoordinates(availablePositions, desiredResult.cordinates);
  return result || {
    /**
     * If all positions are out of boundry then at least provide updated cordintes according
     * last anchor points (that is needed in case when popover goes out of bounds on scrolling)
     */
    cordinates: getCordinates({
      anchors: lastAnchors,
      rects
    })
  };
};

/**
 * Returns an object with padding styles based on the provided `anchors` and `padding`.
 *
 * @param {Object} anchors - An object with `binding` and `content` properties,
 * each with a value of a string representing a vertical and horizontal anchor.
 * @param {number} [padding=10] - The padding value to be applied.
 * @return {Object} An object with a `padding` property, whose value is a string representing
 * the padding values in pixels, separated by spaces.
 *
 * @todo: Think about better padding handling or popover positioning and remove this utility
 */
exports.getPopoverPosition = getPopoverPosition;
const getPaddingStyles = (anchors, padding) => {
  if (!padding) return 0;
  const [bindingAnchorHorizontal, bindingAnchorVertical] = anchors.binding.split('_');
  const [contentAnchorHorizontal, contentAnchorVertical] = anchors.content.split('_');
  const paddings = _objectSpread(_objectSpread({}, {
    top: 0,
    bottom: 0,
    left: 0,
    right: 0
  }), {}, {
    [contentAnchorVertical]: _constants.OPPOSITE_SIDES[bindingAnchorVertical] === contentAnchorVertical ? padding : 0,
    [contentAnchorHorizontal]: _constants.OPPOSITE_SIDES[bindingAnchorHorizontal] === contentAnchorHorizontal ? padding : 0
  });
  return `${paddings.top}px ${paddings.right}px ${paddings.bottom}px ${paddings.left}px`;
};
exports.getPaddingStyles = getPaddingStyles;